import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '../../css/PremierLeagueStandings.css';

const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Allows a user to search for a team by name
function standingNameKey(name) {
    return name
        .trim()
        .replace(/\s*(AFC|FC)\s*$/i, '')
        .trim()
        .toLowerCase()
        .replace(/&/g, ' ')
        .replace(/\./g, '')
        .replace(/'/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}

const PL_STANDING_NAME_TO_SLUG = new Map([
    ['arsenal', 'arsenal'],
    ['aston villa', 'aston-villa'],
    ['bournemouth', 'bournemouth'],
    ['afc bournemouth', 'bournemouth'],
    ['brentford', 'brentford'],
    ['brighton hove albion', 'brighton-hove-albion'],
    ['brighton and hove albion', 'brighton-hove-albion'],
    ['burnley', 'burnley'],
    ['chelsea', 'chelsea'],
    ['crystal palace', 'crystal-palace'],
    ['everton', 'everton'],
    ['fulham', 'fulham'],
    ['leeds united', 'leeds-united'],
    ['leeds', 'leeds-united'],
    ['liverpool', 'liverpool'],
    ['manchester city', 'manchester-city'],
    ['man city', 'manchester-city'],
    ['manchester united', 'manchester-united'],
    ['man united', 'manchester-united'],
    ['man utd', 'manchester-united'],
    ['newcastle united', 'newcastle-united'],
    ['newcastle', 'newcastle-united'],
    ['nottingham forest', 'nottingham-forest'],
    ['nottm forest', 'nottingham-forest'],
    ['sunderland', 'sunderland'],
    ['tottenham hotspur', 'tottenham-hotspur'],
    ['tottenham', 'tottenham-hotspur'],
    ['spurs', 'tottenham-hotspur'],
    ['west ham united', 'west-ham-united'],
    ['west ham', 'west-ham-united'],
    ['wolverhampton wanderers', 'wolverhampton-wanderers'],
    ['wolves', 'wolverhampton-wanderers'],
]);

const PL_ROUTE_SLUGS = new Set(PL_STANDING_NAME_TO_SLUG.values());

function plTeamPageSlugFromStanding(standingName) {
    if (!standingName || typeof standingName !== 'string') return null;
    const key = standingNameKey(standingName);
    if (PL_STANDING_NAME_TO_SLUG.has(key)) return PL_STANDING_NAME_TO_SLUG.get(key);

    const teamStyleSlug = standingName
        .replace(/\s*(FC|AFC)\s*$/i, '')
        .trim()
        .toLowerCase()
        .replace(/ /g, '-');
    if (PL_ROUTE_SLUGS.has(teamStyleSlug)) return teamStyleSlug;

    return null;
}

const PremierLeagueHome = () => {
    const navigate = useNavigate();
    const [standings, setStandings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);

        fetch(`${apiBase}/api/soccer/premier-league/standings`)
            .then((res) => {
                if (!res.ok) {
                    return res.json().then((body) => {
                        throw new Error(body.message || `HTTP ${res.status}`);
                    });
                }
                return res.json();
            })
            .then((data) => {
                if (!cancelled) setStandings(Array.isArray(data) ? data : []);
            })
            .catch((err) => {
                console.error('Error loading standings:', err);
                if (!cancelled) setError(err.message || 'Failed to load standings');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, []);

    const sorted = [...standings].sort((a, b) => Number(a.idx) - Number(b.idx));

    return (
        <main id="pl-standings-content">

            {/* Centered block: matches PL matches page layout */}
            <div className="pl-standings-body">
                <div id="conference-hero-main">
                    <h2 id="quest">Premier League Standings</h2>

                    <div id="nfl-buttons">
                        <Link to="/matches/premier-league" className="nfl-link">
                            <section className="nfl-button-box">
                                <h2>Matches</h2>
                            </section>
                        </Link>

                        <Link to="/teams/premier-league" className="nfl-link">
                            <section className="nfl-button-box">
                                <h2>Teams</h2>
                            </section>
                        </Link>
                    </div>
                </div>

                {loading && (
                    <p style={{ color: '#8899aa', marginBottom: '1rem' }}>Loading standings…</p>
                )}
                {error && (
                    <p style={{ color: '#e53935', marginBottom: '1rem' }} role="alert">
                        {error}
                    </p>
                )}

                <div className="standings-table-wrapper">
                    <table className="standings-table">
                    <thead>
                        <tr>
                            <th className="col-num">#</th>
                            <th>Team</th>
                            <th>MP</th>
                            <th>W</th>
                            <th>D</th>
                            <th>L</th>
                            <th>GF</th>
                            <th>GA</th>
                            <th>GD</th>
                            <th className="col-pts">Pts</th>
                            <th>Form</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map((team) => {
                            const gf = Number(team.gf) || 0;
                            const ga = Number(team.ga) || 0;
                            const gd = Number(team.goalConDiff);
                            const form = team.form_last5 ? team.form_last5.split('') : [];
                            const teamSlug = plTeamPageSlugFromStanding(team.shortName);

                            return (
                                <tr
                                    key={`${team.idx}-${team.shortName}`}
                                    className={teamSlug ? 'standings-row-team--clickable' : undefined}
                                    tabIndex={teamSlug ? 0 : undefined}
                                    aria-label={
                                        teamSlug ? `Open ${team.shortName} team page` : undefined
                                    }
                                    onClick={
                                        teamSlug
                                            ? () =>
                                                  navigate(
                                                      `/teams/premier-league/${teamSlug}`
                                                  )
                                            : undefined
                                    }
                                    onKeyDown={
                                        teamSlug
                                            ? (e) => {
                                                  if (
                                                      e.key === 'Enter' ||
                                                      e.key === ' '
                                                  ) {
                                                      e.preventDefault();
                                                      navigate(
                                                          `/teams/premier-league/${teamSlug}`
                                                      );
                                                  }
                                              }
                                            : undefined
                                    }
                                >
                                    <td className="col-num">
                                        {team.qualColor && (
                                            <span
                                                className="zone-indicator"
                                                style={{ background: team.qualColor }}
                                            />
                                        )}
                                        {team.idx}
                                    </td>
                                    <td className="team-name-cell">
                                        {team.shortName}
                                    </td>
                                    <td>{team.played}</td>
                                    <td>{team.wins}</td>
                                    <td>{team.draws}</td>
                                    <td>{team.losses}</td>
                                    <td>{gf}</td>
                                    <td>{ga}</td>
                                    <td className={gd > 0 ? 'standings-gd-positive' : gd < 0 ? 'standings-gd-negative' : ''}>
                                        {gd > 0 ? `+${gd}` : gd}
                                    </td>
                                    <td className="col-pts">{team.pts}</td>
                                    <td>
                                        <div className="form-cell">
                                            {form.map((result, i) => (
                                                <span key={i} className={`form-dot ${result}`}>
                                                    {result}
                                                </span>
                                            ))}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                    </table>
                </div>
            </div>
        </main>
    );
};

export default PremierLeagueHome;
